import {
  createHostBridge,
  currentContentId,
  type HostBridge,
  isEmbedded,
} from "@emkit/adapter-emdash/bridge";
import { useEffect, useRef, useState } from "react";
import { PERSIST_KEY, useEditor } from "../store.js";

type Status = "idle" | "loading" | "dirty" | "saving" | "saved" | "error";

/**
 * When the editor is mounted inside EmDash's admin iframe, talk to the host
 * instead of localStorage. Mounts an invisible bridge + a floating status
 * indicator. Returns null (no bridge UI) when running standalone.
 */
export function EmbedBridge() {
  const [status, setStatus] = useState<Status>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const bridgeRef = useRef<HostBridge | null>(null);
  const setPage = useEditor((s) => s.setPage);
  const page = useEditor((s) => s.page);
  // Ignore store changes until the host has delivered the initial page —
  // otherwise the blank default would flash as "dirty" on mount.
  const readyRef = useRef(false);

  useEffect(() => {
    if (!isEmbedded()) return undefined;

    setStatus("loading");
    const bridge = createHostBridge();
    bridgeRef.current = bridge;

    const offLoad = bridge.onLoad((loaded) => {
      if (loaded) setPage(loaded);
      readyRef.current = true;
      setStatus("idle");
    });
    const offLoadErr = bridge.onLoadError((msg) => {
      setErrorMessage(`Couldn't load page: ${msg}`);
      // Fail open — let the user start with a blank page rather than
      // showing a permanently empty editor with just an error toast.
      readyRef.current = true;
      setStatus("error");
    });
    const offAck = bridge.onSaveAck(() => setStatus("saved"));
    const offErr = bridge.onSaveError((msg) => {
      setErrorMessage(msg);
      setStatus("error");
    });

    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        const current = useEditor.getState().page;
        setStatus("saving");
        bridge.save(current);
      }
    };
    window.addEventListener("keydown", onKey);

    // If the standalone bootstrap already read a page from localStorage,
    // clear it — the host is the source of truth in embedded mode.
    try {
      window.localStorage.removeItem(PERSIST_KEY);
    } catch {
      /* storage may be denied; ignore */
    }

    return () => {
      offLoad();
      offLoadErr();
      offAck();
      offErr();
      window.removeEventListener("keydown", onKey);
      bridge.destroy();
      bridgeRef.current = null;
    };
    // setPage identity is stable via zustand; safe as a one-shot effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mark dirty on page changes (after the host has loaded the initial state).
  useEffect(() => {
    if (!readyRef.current) return;
    if (!bridgeRef.current) return;
    bridgeRef.current.markDirty(true);
    setStatus("dirty");
  }, [page]);

  if (!isEmbedded()) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: "fixed",
        bottom: 16,
        right: 16,
        zIndex: 9999,
        padding: "8px 14px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 500,
        fontFamily: "inherit",
        background: statusBg(status),
        color: statusFg(status),
        border: `1px solid ${statusBorder(status)}`,
        boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
        pointerEvents: "none",
      }}
      title={
        status === "error"
          ? (errorMessage ?? undefined)
          : status === "dirty"
            ? `Unsaved changes — press ${navigator.platform.includes("Mac") ? "⌘" : "Ctrl"}+S to save`
            : undefined
      }
    >
      {statusLabel(status, currentContentId())}
    </div>
  );
}

function statusLabel(status: Status, contentId: string | null): string {
  switch (status) {
    case "loading":
      return "Loading…";
    case "saving":
      return "Saving…";
    case "saved":
      return "Saved";
    case "dirty":
      return "Unsaved changes";
    case "error":
      return "Save failed";
    default:
      return contentId ? "Ready" : "New page";
  }
}

function statusBg(status: Status): string {
  switch (status) {
    case "saving":
    case "loading":
      return "#eff6ff";
    case "saved":
      return "#ecfdf5";
    case "dirty":
      return "#fffbeb";
    case "error":
      return "#fef2f2";
    default:
      return "#f8fafc";
  }
}

function statusFg(status: Status): string {
  switch (status) {
    case "saving":
    case "loading":
      return "#1e3a8a";
    case "saved":
      return "#064e3b";
    case "dirty":
      return "#78350f";
    case "error":
      return "#7f1d1d";
    default:
      return "#0f172a";
  }
}

function statusBorder(status: Status): string {
  switch (status) {
    case "saving":
    case "loading":
      return "#bfdbfe";
    case "saved":
      return "#bbf7d0";
    case "dirty":
      return "#fde68a";
    case "error":
      return "#fecaca";
    default:
      return "#e2e8f0";
  }
}
